// One entry of a shared logbook trip, read-only: the entry's registry fields as
// plain rows (no form controls -- SharedTripLeg's register), its photos in full,
// and its route map.
//
// The map is TripGroupMap with a single leg rather than TripRouteMap: the group
// map is already an editor-free picture (no add-waypoint, no marker drag, no
// waypoint modal), so read-only needs nothing suppressed.

import { Suspense, useState } from 'react';
import { useParams } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { DataCard, Spinner } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import PhotoViewer from '../../components/PhotoViewer';
import { urlForSharedLogbookTrip } from '../../fleet-shared/Urls.mjs';
import { displayDate } from '../../fleet-shared/Common.mjs';
import {
  entryHeading,
  publicFieldsForType,
  readEntryField,
} from '../../fleet-shared/LogbookTypes.mjs';
import { placeCell } from '../../fleet-shared/PlaceFields.mjs';
import {
  ENTRY_ROUTE_KEY,
  routeFromFields,
  hasPlacedWaypoints,
} from '../../fleet-shared/TripRoute.mjs';
import EntryPhotoStrip from '../logbook/EntryPhotoStrip';
import DownloadAllPhotos from './DownloadAllPhotos';
import SharedPhotoUpload from './SharedPhotoUpload';
import SharedBackLink from './SharedBackLink';
import { useSharedLogbookTrip } from './SharedLogbookTrip';
import { zipFileName } from '../../utilities/photoZip.mjs';
import { shareUploadAvailable } from '../../utilities/shareUpload.mjs';

// Lazy: the only import path here that pulls in maplibre-gl (~250 KB gz).
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

// One field row's value, the shared-page register: dates formatted, a gazetteer
// name shortened with the full one on `title`, a flag as a word.
function fieldValue(entry, field, placeIndex) {
  const value = readEntryField(entry, field);
  if (value === null || value === undefined || value === '') return null;
  if (field.type === 'date') return { text: displayDate(value) };
  if (field.type === 'place') return placeCell(value, placeIndex);
  if (field.type === 'boolean') return { text: value ? 'Yes' : 'No' };
  return { text: String(value) };
}

const SharedLogbookEntry = () => {
  const { entryId } = useParams();
  const {
    shareId, name, typeId, displayEntries, mapLegs, placeIndex, photosByEntry, photoTotals,
    uploadsAllowed, addPhotos,
  } = useSharedLogbookTrip();

  // Which of this entry's photos the viewer is showing. Held here rather than in
  // the strip so the arrows can walk the whole set.
  const [viewing, setViewing] = useState(null);

  const entries = displayEntries ?? [];
  const index = entries.findIndex((candidate) => candidate.id === entryId);
  const entry = index >= 0 ? entries[index] : null;

  const backLink = (
    <SharedBackLink to={urlForSharedLogbookTrip(shareId)}>
      Back to {name}
    </SharedBackLink>
  );

  if (!entry) {
    return (
      <>
        {backLink}
        <p className="text-center text-body-secondary py-5">
          This entry is not part of this trip.
        </p>
      </>
    );
  }

  const note = typeof entry.notes === 'string' ? entry.notes.trim() : '';
  const leg = (mapLegs ?? []).find((candidate) => candidate.id === entry.id);
  const onMap = Boolean(leg) && hasPlacedWaypoints(routeFromFields(leg.fields, ENTRY_ROUTE_KEY));

  const photos = photosByEntry?.get(entry.id) ?? [];
  // What the Lambda's per-entry cap left behind. Said rather than silently
  // dropped: a capped strip is indistinguishable from a complete one.
  const withheld = Math.max(0, (photoTotals?.get(entry.id) ?? photos.length) - photos.length);

  // Notes is a registry field on every type, and it gets its own row below with
  // pre-wrap, so it must not also come out of this loop.
  //
  // publicFieldsForType, not fieldsForType: this loop is BLIND -- any registered
  // key the payload carries becomes a row -- so it is the second half of the
  // doubled whitelist, reading the same rule the Lambda redacts by.
  const rows = publicFieldsForType(typeId)
    .filter((field) => field.key !== 'notes')
    .map((field) => ({ field, value: fieldValue(entry, field, placeIndex) }))
    // A blank row asserts nothing. Nine of them on a read-only page is worse
    // than a short table.
    .filter(({ value }) => value !== null);

  return (
    <>
      {backLink}

      <DataCard
        header={<CardHeader text={entryHeading(entry, typeId, { placeIndex })} />}
        body={
          <Table className="align-middle mb-0">
            <tbody>
              {rows.map(({ field, value }) => (
                <tr key={field.key}>
                  <th scope="row" className="text-body-secondary" style={{ width: 140 }}>
                    {field.name}
                  </th>
                  <td className="entered-data" title={value.title}>{value.text}</td>
                </tr>
              ))}
              {note && (
                <tr>
                  <th scope="row" className="text-body-secondary">Notes</th>
                  <td className="entered-data" style={{ whiteSpace: 'pre-wrap' }}>{note}</td>
                </tr>
              )}
            </tbody>
          </Table>
        }
      />

      {photos.length > 0 && (
        <DataCard
          header={
            // "For pages where the photos have a header, it can be in the
            // header." `extraActions` already exists for exactly this and
            // renders first in the actions row.
            //
            // One GROUP, with no folder, so this entry's zip is flat -- the trip
            // page's foldering exists to keep two days' IMG_1234.jpg apart, and
            // there is only one day here.
            <CardHeader
              text="Photos"
              extraActions={
                <DownloadAllPhotos
                  groups={[{ photos }]}
                  withheld={withheld}
                  zipName={zipFileName(`${name} ${entry.date ? entry.date.slice(0, 10) : ''}`)}
                />
              }
            />
          }
          body={
            <>
              {/* Infinity: this page IS where "+N more" points, so that tile
                  would link to itself. */}
              <EntryPhotoStrip
                photos={photos}
                limit={Infinity}
                onOpenPhoto={(photo) => {
                  const index = photos.findIndex((candidate) => candidate.key === photo.key);
                  if (index >= 0) setViewing(index);
                }}
                onOpenEntry={() => {}}
              />
              {withheld > 0 && (
                <p className="small text-body-secondary mt-2 mb-0">
                  {withheld} more {withheld === 1 ? 'photo isn' : 'photos aren'}&apos;t shown.
                </p>
              )}
            </>
          }
        />
      )}

      {onMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap
            legs={[leg]}
            onSelectLeg={() => {}}
            routeKey={ENTRY_ROUTE_KEY}
            title="Route"
            showWeather={false}
          />
        </Suspense>
      )}

      {/* This day is already chosen, so no picker. It is mounted OUTSIDE the
          Photos card's `photos.length > 0` guard on purpose: an entry with no
          photos yet is exactly the one most worth adding to, and putting the
          control inside that card would hide it there. */}
      {shareUploadAvailable() && uploadsAllowed && (
        <SharedPhotoUpload shareId={shareId} entryId={entry.id} onAdded={addPhotos} />
      )}

      <PhotoViewer
        photos={photos}
        index={viewing}
        onIndex={setViewing}
        onHide={() => setViewing(null)}
      />
    </>
  );
};

export default SharedLogbookEntry;
